import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, Search, Compass, FileText, Mic, CheckCircle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const HERO_IMG = "https://images.unsplash.com/photo-1760960553755-0f355491749e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxpdnklMjBsZWFndWUlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXV0dW1ufGVufDB8fHx8MTc3Mzg4Nzc1M3ww&ixlib=rb-4.1.0&q=85";

const phases = [
  {
    number: "I",
    title: "Discovery & Diagnosis",
    icon: Search,
    items: [
      "Comprehensive strategy sessions to uncover intellectual drivers, academic positioning, long-term objectives, and narrative themes",
      "Gather school counselor information, analyze the school profile, and understand institutional context",
      "Admissions decisions are contextual. Our strategy must be as well.",
    ],
  },
  {
    number: "II",
    title: "Strategic Positioning",
    icon: Compass,
    intro: "We construct a unified framework that guides:",
    items: [
      "Course rigor decisions",
      "Extracurricular prioritization",
      "Passion project development",
      "Summer programs and enrichment pathways",
      "School list calibration",
    ],
    footer: "We leverage our internal library of vetted programs, research opportunities, competitions, leadership pathways, and enrichment initiatives to strengthen narrative coherence — not clutter.",
  },
  {
    number: "III",
    title: "Application Architecture",
    icon: FileText,
    items: [
      "Essays are structured arguments",
      "Materials reviewed for logical clarity, thematic alignment, persuasive flow, and institutional resonance",
      "Former admissions interviewers assess how the application will read inside committee deliberations",
      "Senior advisors pressure-test materials before submission",
    ],
  },
  {
    number: "IV",
    title: "Presentation & Defense",
    icon: Mic,
    items: [
      "Interview preparation focuses on composure, articulation, narrative consistency, and institutional awareness",
      "Confidence is engineered through preparation and repetition",
    ],
  },
];

export default function CaseMethodPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="case-method-page">
      <SEO
        title="The Case Method — Our Ivy League Admissions Framework"
        description="Four phases of disciplined Ivy League admissions strategy: Discovery, Positioning, Application Architecture, Presentation. Built by former Harvard interviewers."
        keywords="ivy league admissions strategy, application framework, harvard admissions method, college application strategy"
      />
      {/* Nav */}
      <MarketingNav />

      {/* Hero with Image */}
      <section className="pt-16 relative overflow-hidden">
        <div className="relative h-[60vh] min-h-[450px] max-h-[600px]">
          <img src={HERO_IMG} alt="Ivy League campus clock tower" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B] via-[#0F392B]/75 to-[#0F392B]/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-6">How It Works</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Case Method
              </h1>
              <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
                We approach every application like a high-stakes case. Transcript as evidence. Activities as exhibits. Essays as argument. Recommendations as testimony.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Method Intro */}
      <section className="py-16 bg-white border-b border-[#E2E2E2]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Quote className="h-8 w-8 text-[#D4AF37]/30 mx-auto mb-4" strokeWidth={1} />
          <p className="text-xl md:text-2xl text-[#0F392B] font-medium leading-snug italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            "Every decision must reinforce the central thesis of the application."
          </p>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {phases.map((phase) => (
              <div key={phase.number} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-testid={`phase-${phase.number}`}>
                <div className="md:col-span-3">
                  <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-3">
                    <span className="text-sm font-mono text-[#D4AF37] font-medium tracking-wide">Phase {phase.number}</span>
                    <div className="w-12 h-12 rounded-xl bg-[#0F392B] flex items-center justify-center">
                      <phase.icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="text-2xl md:text-3xl font-medium mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{phase.title}</h3>
                  {phase.intro && <p className="text-base text-muted-foreground mb-4">{phase.intro}</p>}
                  <div className="space-y-3">
                    {phase.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#F9F9F7] transition-colors">
                        <CheckCircle className="h-4 w-4 text-[#0F392B] mt-1 flex-shrink-0" strokeWidth={2} />
                        <p className="text-base leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                  {phase.footer && <p className="text-sm text-muted-foreground mt-5 leading-relaxed italic border-l-2 border-[#D4AF37]/30 pl-4">{phase.footer}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with Image */}
      <section className="relative h-[45vh] min-h-[350px]">
        <img src="https://images.unsplash.com/photo-1607874846377-695fd1e2aeae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxpdnklMjBsZWFndWUlMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXV0dW1ufGVufDB8fHx8MTc3Mzg4Nzc1M3ww&ixlib=rb-4.1.0&q=85" alt="University campus" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F392B]/85" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Serious cases require <span className="italic">layered insight.</span>
            </h2>
            <p className="text-base text-white/70 mb-8 max-w-xl mx-auto">
              No matter which package a family selects, the application is examined by multiple experienced reviewers. Admissions committees deliberate collectively. So do we.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/sign-up">
                <Button size="lg" className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base shadow-xl" data-testid="case-method-cta-btn">
                  Build My Strategy <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="h-12 px-8 text-base border-white/25 text-white hover:bg-white/10">View Packages</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
            <span className="font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/portal" className="hover:text-foreground transition-colors">$99 Portal</Link>
            <Link to="/premium" className="hover:text-foreground transition-colors">Premium</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Packages</Link>
          </div>
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Ivy League Consulting. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
